import { useTranslation } from 'react-i18next';
import { Globe, Share2, Video } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n/languages';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Chip } from './Chip';
import { initialsOf } from '../helpers';
import type { PublicOrganization } from '../types';

interface BusinessHeaderProps {
  organization: PublicOrganization;
  // No photos (or photos hidden in an embed): show the logo hero instead.
  hero: boolean;
}

export function LogoTile({ organization, size = 'md' }: { organization: PublicOrganization; size?: 'sm' | 'md' | 'lg' }) {
  const box = size === 'lg' ? 'h-24 w-24 rounded-[22px] text-[42px]' : size === 'sm' ? 'h-10 w-10 rounded-lg text-lg' : 'h-16 w-16 rounded-2xl text-2xl';
  if (organization.logo_url) {
    return (
      <span className={cn('flex shrink-0 items-center justify-center overflow-hidden border border-line bg-surface', box)}>
        <img src={organization.logo_url} alt={organization.name} className="h-full w-full object-contain p-1.5" />
      </span>
    );
  }
  return (
    <span
      className={cn('flex shrink-0 items-center justify-center border border-brand-100 bg-surface font-display font-semibold text-brand-700', box)}
      data-testid="logo-initial"
      aria-hidden="true"
    >
      {initialsOf(organization.name).slice(0, 1)}
    </span>
  );
}

export function BusinessHeader({ organization, hero }: BusinessHeaderProps) {
  const { t } = useTranslation('bookingPage');
  const { toast } = useToast();
  const page = organization.booking_page;
  const languages = SUPPORTED_LANGUAGES.filter((l) => organization.public_locales?.includes(l.code)).map((l) => l.name);

  const share = async () => {
    const url = window.location.href.split('?')[0];
    if (navigator.share) {
      await navigator.share({ title: organization.name, url }).catch(() => undefined);
    } else {
      await navigator.clipboard?.writeText(url);
      toast({ title: t('header.linkCopied') });
    }
  };

  const chips = (
    <div className="mt-1.5 flex flex-wrap gap-2" data-testid="header-chips">
      {page.hours && (page.hours.open_now && page.hours.closes_at ? (
        <Chip tone="success" data-testid="chip-open-now">{t('chips.openUntil', { time: page.hours.closes_at })}</Chip>
      ) : (
        <Chip data-testid="chip-closed">{t('chips.closedNow')}</Chip>
      ))}
      {page.show_online && <Chip icon={<Video className="h-3.5 w-3.5" />} data-testid="chip-online">{t('chips.online')}</Chip>}
      {/* The joined language list is the widest chip by far and never wraps, so
          on a phone it would push past the screen edge. The header's language
          switcher already covers picking one; this chip is extra detail. */}
      {languages.length > 1 && (
        <Chip icon={<Globe className="h-3.5 w-3.5" />} className="hidden sm:inline-flex">{languages.join(', ')}</Chip>
      )}
    </div>
  );

  const shareButton = (
    <button
      type="button"
      onClick={share}
      aria-label={t('header.share')}
      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-pill border border-line bg-surface text-ink hover:border-brand-300"
    >
      <Share2 className="h-[18px] w-[18px]" />
    </button>
  );

  if (hero) {
    return (
      <section className="flex flex-col gap-5 rounded-4xl bg-brand-50 p-6 sm:flex-row sm:items-center sm:gap-7 sm:p-10" data-testid="logo-hero">
        <LogoTile organization={organization} size="lg" />
        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <h1 className="font-display text-[24px] font-semibold leading-tight tracking-tight text-ink sm:text-[40px]">{organization.name}</h1>
          {page.tagline && <p className="text-body-lg text-soft">{page.tagline}</p>}
          {chips}
        </div>
        <div className="hidden sm:block">{shareButton}</div>
      </section>
    );
  }

  return (
    <div className="flex items-end justify-between gap-6">
      <div className="flex min-w-0 flex-col gap-2.5">
        <h1 className="font-display text-[24px] font-semibold leading-tight tracking-tight text-ink sm:text-[40px]">{organization.name}</h1>
        {page.tagline && <p className="text-body-lg text-soft sm:text-[17px]">{page.tagline}</p>}
        {chips}
      </div>
      <div className="hidden sm:block">{shareButton}</div>
    </div>
  );
}
